import { memo } from "react";

import { ProductFormPlaceholder } from "@react/sections/ProductHero/ProductForm/ProductFormPlaceholder.view";
import { ProductSelectorPlaceholder } from "@react/sections/ProductHero/ProductSelector/ProductSelectorPlaceholder.view";
import { type ProductHeroSettings } from "@root/tablescape/state";
import { type TTailwindString, tw } from "classnames";

type Props = {
  animateIn: boolean;
  region: "section" | "component" | "zone";
} & Pick<
  ProductHeroSettings["settings"],
  | "addonUpsellProductsCount"
  | "crossProductBridgeOptionsCount"
  | "productOptionCategoriesCount"
  | "productSelectorExperience"
  | "showAddonUpsell"
  | "showCrossProductBridge"
  | "theme"
> &
  Pick<ProductHeroSettings["productGalleryCarousel"], "lcpMedia">;

const styles = {
  base: tw(
    "pointer-events-none",
    "box-border",
    "bg-background-primary",
    "pb-16",
    "mg:pb-24",
    "mg:pt-12"
  ),
  container: ({ animateIn }: { animateIn: boolean }) =>
    tw(
      "mx-auto",
      "max-w-screen-md",
      "animate-pulse",
      "mg:grid",
      "mg:max-w-screen-xl",
      "mg:grid-cols-[auto_464px]" as TTailwindString,
      "mg:grid-rows-[auto_auto_1fr]" as TTailwindString,
      "mg:gap-x-[116px]" as TTailwindString,
      "mg:pl-12",
      "mg:pr-12",
      {
        "animate-fade-in": animateIn,
        "duration-1000": animateIn,
      }
    ),
  image: tw(
    "flex",
    "h-full",
    "w-full",
    "items-center",
    "justify-center",
    "rounded",
    "bg-placeholder"
  ),
  svg: tw("h-10", "w-10", "text-placeholder"),
};

const ProductHeroImagePlaceholder = () => (
  <div className={styles.image} data-placeholder-region="component">
    <svg
      aria-hidden="true"
      className={styles.svg}
      fill="currentColor"
      viewBox="0 0 20 18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
    </svg>
  </div>
);

const ProductHeroPlaceholderBase = ({
  addonUpsellProductsCount,
  animateIn,
  crossProductBridgeOptionsCount,
  lcpMedia,
  productOptionCategoriesCount,
  productSelectorExperience,
  region,
  showAddonUpsell,
  showCrossProductBridge,
  theme,
}: Props) => (
  <div
    className={tw(
      "pointer-events-none",
      "box-border",
      "bg-background-primary",
      "pb-16",
      "mg:pb-24",
      "mg:pt-12"
    )}
    data-placeholder-region={region}
    data-theme={theme}
  >
    <div
      className={tw(
        "mx-auto",
        "max-w-screen-md",
        "mg:grid",
        "mg:max-w-screen-xl",
        "mg:grid-cols-[auto_464px]" as TTailwindString,
        "mg:grid-rows-[auto_auto_1fr]" as TTailwindString,
        "mg:gap-x-[116px]" as TTailwindString,
        "mg:pl-12",
        "mg:pr-12",
        {
          "animate-fade-in": animateIn,
          "duration-1000": animateIn,
        }
      )}
    >
      {/* Breadcrumb Placeholder */}
      <div
        className={tw("animate-pulse", "mb-4", "mt-1", "hidden", "mg:block")}
      >
        <div className={tw("flex", "w-full", "items-center", "gap-4")}>
          <div
            className={tw(
              "h-2",
              "w-full",
              "max-w-[30px]" as TTailwindString,
              "rounded-full",
              "bg-placeholder"
            )}
          ></div>
          <div
            className={tw(
              "h-2",
              "w-full",
              "max-w-[60px]" as TTailwindString,
              "rounded-full",
              "bg-placeholder"
            )}
          ></div>
          <div
            className={tw(
              "h-2",
              "w-full",
              "max-w-[80px]" as TTailwindString,
              "rounded-full",
              "bg-placeholder"
            )}
          ></div>
        </div>
      </div>

      {/* Product Gallery Placeholder */}
      <div className={tw("mg:row-span-2", "mg:row-start-2")}>
        {/* Product Gallery Grid */}
        <div className={tw("hidden", "mg:block")}>
          <div
            className={tw(
              "grid",
              "w-full",
              "max-w-[764px]" as TTailwindString,
              "grid-cols-2",
              "gap-4"
            )}
          >
            <div
              className={tw(
                "relative",
                "col-span-2",
                "flex",
                "h-full",
                "w-full",
                "items-center",
                "md:aspect-[764/577]" as TTailwindString
              )}
            >
              {lcpMedia.sources[0]?.src && (
                <img
                  alt={lcpMedia.altText}
                  className={tw("h-full", "object-cover", "object-center-60")}
                  decoding="async"
                  // eslint-disable-next-line react/no-unknown-property
                  fetchpriority="high"
                  height="767"
                  loading="eager"
                  src={lcpMedia.sources[0].src}
                  srcSet={lcpMedia.sources
                    .map(
                      ({ devicePixelRatio, src }) =>
                        `${src} ${devicePixelRatio}x`
                    )
                    .join(", ")}
                  width="767"
                />
              )}
            </div>
            <div className={tw("aspect-square")}>
              <ProductHeroImagePlaceholder />
            </div>
            <div className={tw("aspect-square")}>
              <ProductHeroImagePlaceholder />
            </div>
            <div
              className={tw("col-span-2", "flex", "w-full", "justify-center")}
            >
              <div
                className={tw(
                  "h-[155px]" as TTailwindString,
                  "w-full",
                  "rounded",
                  "bg-placeholder"
                )}
              ></div>
            </div>
            <div className={tw("aspect-square")}>
              <ProductHeroImagePlaceholder />
            </div>
            <div className={tw("aspect-square")}>
              <ProductHeroImagePlaceholder />
            </div>
          </div>
        </div>

        {/* Product Gallery Carousel */}
        <div className={tw("mg:hidden")}>
          <div className={tw("relative", "overflow-hidden")}>
            <div
              className={tw(
                "h-[376px]" as TTailwindString,
                "relative",
                "overflow-hidden",
                "w-full",
                "md:h-[767px]" as TTailwindString
              )}
            >
              {lcpMedia.sources[0]?.src && (
                <img
                  alt={lcpMedia.altText}
                  className={tw("flex", "w-full", "h-auto", "aspect-square")}
                  decoding="async"
                  // eslint-disable-next-line react/no-unknown-property
                  fetchpriority="high"
                  height="767"
                  loading="eager"
                  src={lcpMedia.sources[0].src}
                  srcSet={lcpMedia.sources
                    .map(
                      ({ devicePixelRatio, src }) =>
                        `${src} ${devicePixelRatio}x`
                    )
                    .join(", ")}
                  width="767"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Product Intro Placeholder */}
      <div
        className={tw(
          "animate-pulse",
          "box-border",
          "px-6",
          "pt-4",
          "md:mb-4.5",
          "mg:row-start-2",
          "mg:mb-2.5",
          "mg:px-0",
          "mg:pt-0"
        )}
      >
        <div className={tw("mb-4")}>
          {/* Product Intro Heading */}
          <div
            className={tw(
              "mb-4",
              "flex",
              "h-6",
              "items-center",
              "md:mb-6",
              "md:mt-2",
              "mg:mb-7"
            )}
          >
            <div
              className={tw(
                "h-5",
                "w-48",
                "rounded-full",
                "bg-placeholder",
                "md:h-8",
                "md:w-72"
              )}
            ></div>
          </div>

          {/* Product Intro Subheading */}
          <div
            className={tw(
              "mb-2.5",
              "h-2",
              "max-w-[480px]" as TTailwindString,
              "rounded-full",
              "bg-placeholder",
              "md:h-3",
              "mg:mb-3.5"
            )}
          ></div>

          {/* Product Intro Review Stars */}
          <div
            className={tw(
              "mb-3",
              "flex",
              "h-7",
              "items-center",
              "gap-2",
              "md:mb-4",
              "mg:mb-3"
            )}
          >
            <div
              className={tw(
                "h-3.5",
                "w-full",
                "max-w-[80px]" as TTailwindString,
                "rounded-full",
                "bg-placeholder"
              )}
            ></div>
            <div
              className={tw(
                "h-2",
                "w-full",
                "max-w-[120px]" as TTailwindString,
                "rounded-full",
                "bg-placeholder"
              )}
            ></div>
          </div>

          {/* Product Intro Price */}
          <div
            className={tw(
              "-mb-[3px]" as TTailwindString,
              "flex",
              "h-7",
              "items-center",
              "gap-2",
              "md:-mb-[1px]" as TTailwindString
            )}
          >
            <div
              className={tw(
                "h-3",
                "w-full",
                "max-w-[40px]" as TTailwindString,
                "rounded-full",
                "bg-placeholder"
              )}
            ></div>
            <div
              className={tw(
                "h-3",
                "w-full",
                "max-w-[80px]" as TTailwindString,
                "rounded-full",
                "bg-placeholder"
              )}
            ></div>
            <div
              className={tw(
                "h-5",
                "w-full",
                "max-w-[120px]" as TTailwindString,
                "rounded-full",
                "bg-placeholder"
              )}
            ></div>
          </div>
        </div>

        {/* Product Intro Divider */}
        <div className={tw("mb-4", "mg:mb-6")}>
          {/* Cross-Product Bridge Placeholder */}
          {showCrossProductBridge && (
            <div className={tw("w-full", "flex", "flex-col", "gap-4")}>
              <div className={tw("flex", "items-baseline", "gap-2")}>
                <div
                  className={tw("h-4", "w-10", "rounded", "bg-placeholder")}
                ></div>
                <div
                  className={tw("h-4", "w-24", "rounded", "bg-placeholder")}
                ></div>
              </div>
              <div
                className={tw(
                  {
                    "grid-cols-1": crossProductBridgeOptionsCount === 1,
                    "grid-cols-2":
                      crossProductBridgeOptionsCount === 2 ||
                      crossProductBridgeOptionsCount > 3,
                    "grid-cols-3": crossProductBridgeOptionsCount === 3,
                  },
                  "grid",
                  "gap-2",
                  "md:flex",
                  "md:flex-nowrap",
                  "w-full",
                  "list-none",
                  "m-0",
                  "p-0"
                )}
              >
                {Array.from({ length: crossProductBridgeOptionsCount }).map(
                  (_, index) => (
                    <div className={tw("flex", "w-full")} key={index}>
                      <div
                        className={tw(
                          "flex",
                          "h-[42px]" as TTailwindString,
                          "w-full",
                          "rounded",
                          "border",
                          "border-solid",
                          "border-placeholder",
                          "bg-placeholder"
                        )}
                      ></div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {productSelectorExperience === "combo_selector" ? (
          <>
            {/* Product Selector Placeholder */}
            <ProductSelectorPlaceholder
              addonUpsellProductsCount={addonUpsellProductsCount}
              showAddonUpsell={showAddonUpsell}
            />
          </>
        ) : (
          <>
            {/* Product Form Placeholder */}
            <ProductFormPlaceholder
              addonUpsellProductsCount={addonUpsellProductsCount}
              productOptionCategoriesCount={productOptionCategoriesCount}
              showAddonUpsell={showAddonUpsell}
            />
          </>
        )}
      </div>

      {/* Product Overview Placeholder */}
      <div
        className={tw(
          "animate-pulse",
          "box-border",
          "border-0",
          "px-6",
          "pt-9",
          "mg:row-start-3",
          "mg:border-t",
          "mg:border-solid",
          "mg:border-placeholder",
          "mg:px-0",
          "mg:pt-8"
        )}
      >
        {/* Breadcrumb Placeholder */}
        <div className={tw("mb-4", "mg:hidden")}>
          <div className={tw("flex", "w-full", "items-center", "gap-4")}>
            <div
              className={tw(
                "h-2",
                "w-full",
                "max-w-[30px]" as TTailwindString,
                "rounded-full",
                "bg-placeholder"
              )}
            ></div>
            <div
              className={tw(
                "h-2",
                "w-full",
                "max-w-[60px]" as TTailwindString,
                "rounded-full",
                "bg-placeholder"
              )}
            ></div>
            <div
              className={tw(
                "h-2",
                "w-full",
                "max-w-[80px]" as TTailwindString,
                "rounded-full",
                "bg-placeholder"
              )}
            ></div>
          </div>
        </div>

        {/* Product Overview Heading */}
        <div className={tw("mb-7")}>
          <div
            className={tw(
              "h-4",
              "w-full",
              "max-w-[140px]" as TTailwindString,
              "rounded-full",
              "bg-placeholder"
            )}
          ></div>
        </div>

        {/* Product Overview Content */}
        <div
          className={tw(
            "border-0",
            "border-b",
            "border-solid",
            "border-placeholder"
          )}
        >
          <div
            className={tw("mb-7", "flex", "flex-col", "gap-2.5", "mg:gap-3.5")}
          >
            <div
              className={tw("h-2", "w-full", "rounded-full", "bg-placeholder")}
            ></div>
            <div
              className={tw(
                "h-2",
                "w-full",
                "rounded-full",
                "bg-placeholder",
                "md:hidden",
                "mg:block"
              )}
            ></div>
            <div
              className={tw(
                "h-2",
                "w-full",
                "max-w-[50%]" as TTailwindString,
                "rounded-full",
                "bg-placeholder"
              )}
            ></div>
          </div>
          <div
            className={tw(
              "mb-7",
              "ml-6",
              "flex",
              "flex-col",
              "gap-2.5",
              "mg:gap-3"
            )}
          >
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                className={tw(
                  "h-2",
                  "w-full",
                  "rounded-full",
                  "bg-placeholder"
                )}
                key={index}
              ></div>
            ))}
            {Array.from({ length: 2 }).map((_, index) => (
              <div
                className={tw(
                  "h-2",
                  "w-full",
                  "rounded-full",
                  "bg-placeholder",
                  "md:hidden"
                )}
                key={index}
              ></div>
            ))}
            <div
              className={tw(
                "h-2",
                "w-full",
                "rounded-full",
                "bg-placeholder",
                "md:hidden",
                "mg:block"
              )}
            ></div>
            <div
              className={tw(
                "h-2",
                "w-full",
                "max-w-[50%]" as TTailwindString,
                "rounded-full",
                "bg-placeholder"
              )}
            ></div>
          </div>
          <div
            className={tw(
              "mb-8",
              "ml-6",
              "flex",
              "flex-col",
              "gap-2.5",
              "mg:gap-3"
            )}
          >
            <div
              className={tw(
                "h-16",
                "w-full",
                "rounded",
                "bg-placeholder",
                "mg:h-18"
              )}
            ></div>
          </div>
        </div>

        {/* Product UGC Carousel */}
        <div className={tw("mb-6", "pt-6", "md:pt-9")}>
          <div
            className={tw(
              "mb-6",
              "h-3",
              "w-full",
              "max-w-[160px]" as TTailwindString,
              "rounded-full",
              "bg-placeholder",
              "md:mb-9"
            )}
          ></div>

          <div className={tw("flex", "gap-3", "overflow-hidden")}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                className={tw(
                  "h-[180px]" as TTailwindString,
                  "w-[110px]" as TTailwindString,
                  "flex-shrink-0",
                  "rounded-lg",
                  "border-0",
                  "bg-placeholder",
                  "md:h-[195px]" as TTailwindString,
                  "md:w-30"
                )}
                key={index}
              ></div>
            ))}
          </div>
        </div>

        {/* Product Overview Accordion */}
        <div className={tw("mb-12", "flex", "flex-col", "mg:mt-6")}>
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              className={tw(
                "flex",
                "h-[61px]" as TTailwindString,
                "w-full",
                "items-center",
                "border-0",
                "border-b",
                "border-solid",
                "border-placeholder"
              )}
              key={index}
            >
              <div
                className={tw(
                  "h-2",
                  "w-[40%]" as TTailwindString,
                  "rounded",
                  "bg-placeholder"
                )}
              ></div>
            </div>
          ))}
        </div>

        <div className={tw("flex", "h-80", "w-full", "md:h-72", "mg:hidden")}>
          <ProductHeroImagePlaceholder />
        </div>
      </div>
    </div>
  </div>
);

export const ProductHeroPlaceholder = memo(ProductHeroPlaceholderBase);
